import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import AnimeCard from "@/components/feature/AnimeCard";
import usePageMeta from "@/hooks/usePageMeta";
import { animeList, categoryMeta, type Anime } from "@/mocks/anime";
import FilterBar, { type FilterState } from "@/pages/category/components/FilterBar";

const PAGE_SIZE = 8;
const sortOptions = [
  { key: "updated", label: "默认 · 按更新时间" },
  { key: "hot", label: "按播放热度" },
  { key: "rating", label: "按评分" },
];

export default function CategoryPage() {
  const { type } = useParams();
  const meta = categoryMeta.find((item) => item.slug === type) || categoryMeta[0];
  const [filters, setFilters] = useState<FilterState>({ type: "全部", region: "全部", year: "全部", status: "全部" });
  const [sortBy, setSortBy] = useState("updated");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  useEffect(() => {
    setFilters({ type: "全部", region: "全部", year: "全部", status: "全部" });
    setSortBy("updated");
    setVisible(PAGE_SIZE);
  }, [type]);

  usePageMeta(
    `妖精动漫 - ${meta.name}分类 - 最新作品推荐`,
    `${meta.name}分类收录妖精动漫站内的${meta.desc}，支持按类型、地区、年份与连载状态筛选，快速找到想看的动画作品。`
  );

  const baseList = useMemo(() => {
    if (meta.slug === "guoman") return animeList.filter((a) => a.region === "国漫");
    if (meta.slug === "riman") return animeList.filter((a) => a.region === "日漫");
    if (meta.slug === "hanman") return animeList.filter((a) => a.region === "韩漫");
    return animeList.filter((a) => a.type === meta.type);
  }, [meta]);

  const filtered = useMemo(() => {
    let list: Anime[] = baseList.filter((item) => {
      if (filters.type !== "全部" && item.type !== filters.type) return false;
      if (filters.region !== "全部" && item.region !== filters.region) return false;
      if (filters.year !== "全部" && String(item.year) !== filters.year) return false;
      if (filters.status !== "全部" && item.status !== filters.status) return false;
      return true;
    });
    if (sortBy === "hot") list = [...list].sort((a, b) => b.views - a.views);
    else if (sortBy === "rating") list = [...list].sort((a, b) => b.rating - a.rating);
    else list = [...list].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
    return list;
  }, [baseList, filters, sortBy]);

  const shown = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  const resetAll = () => {
    setFilters({ type: "全部", region: "全部", year: "全部", status: "全部" });
    setSortBy("updated");
    setVisible(PAGE_SIZE);
  };

  return (
    <SiteLayout>
      <section className="w-full bg-primary-950">
        <div className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="[&_a]:text-primary-300 [&_span]:text-background-50">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "动漫分类", to: "/category/guoman" }, { label: meta.name }]} />
          </div>
          <div className="mt-5 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-black tracking-tight text-background-50">
                {meta.name}分类
              </h1>
              <p className="mt-2 text-[13px] md:text-[14px] leading-7 text-primary-200 max-w-[640px]">
                {meta.desc}。共收录 {baseList.length} 部作品，支持多维筛选与排序，点击卡片查看完整剧集与简介。
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {categoryMeta.slice(0, 4).map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    cat.slug === meta.slug
                      ? "bg-accent-500 text-primary-950"
                      : "bg-primary-900 text-primary-100 hover:bg-primary-800"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-8 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 lg:gap-8">
          <aside className="hidden lg:block">
            <div className="sticky top-[88px] space-y-4">
              <FilterBar value={filters} onChange={(v) => { setFilters(v); setVisible(PAGE_SIZE); }} onReset={resetAll} resultCount={filtered.length} />
              <div className="rounded-lg bg-background-100 border border-background-200 p-4">
                <h2 className="text-[13px] font-semibold text-foreground-950 mb-3">热门标签</h2>
                <div className="flex flex-wrap gap-1.5">
                  {["机甲", "国风", "热血", "治愈", "江湖", "群像", "悬疑", "美食"].map((tag) => (
                    <span key={tag} className="px-2 py-1 rounded-md bg-background-50 border border-background-200 text-[11px] text-foreground-600">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          <div className="min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setMobileFilterOpen((v) => !v)}
                  className="lg:hidden inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md bg-primary-900 text-background-50 text-[12px] font-semibold whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-filter-3-line" /> 筛选
                </button>
                <p className="text-[13px] text-foreground-600">
                  找到 <span className="font-bold text-foreground-950">{filtered.length}</span> 部作品
                </p>
              </div>
              <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200 overflow-x-auto no-scrollbar">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setSortBy(opt.key)}
                    className={`px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      sortBy === opt.key ? "bg-background-50 text-primary-800 border border-primary-200" : "text-foreground-600 hover:text-foreground-950"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {mobileFilterOpen && (
              <div className="lg:hidden mb-5 animate-fade-up">
                <FilterBar value={filters} onChange={(v) => { setFilters(v); setVisible(PAGE_SIZE); }} onReset={resetAll} resultCount={filtered.length} />
              </div>
            )}

            {filtered.length === 0 ? (
              <div className="rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
                <span className="w-14 h-14 mx-auto rounded-full bg-background-200 flex items-center justify-center text-foreground-500 flex items-center justify-center">
                  <i className="ri-emotion-sad-line text-2xl" />
                </span>
                <h3 className="mt-4 text-[15px] font-semibold text-foreground-950">没有符合条件的作品</h3>
                <p className="mt-2 text-[13px] text-foreground-500">试试放宽筛选条件，或者重置后重新浏览。</p>
                <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={resetAll}
                    className="h-9 px-4 rounded-md bg-accent-500 hover:bg-accent-600 text-primary-950 text-[13px] font-bold transition-colors whitespace-nowrap cursor-pointer"
                  >
                    重置筛选条件
                  </button>
                  <Link
                    to="/search"
                    className="h-9 px-4 rounded-md border border-background-300 text-foreground-700 text-[13px] font-medium inline-flex items-center transition-colors whitespace-nowrap cursor-pointer hover:bg-background-50"
                  >
                    去搜索页找找
                  </Link>
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
                  {shown.map((anime) => (
                    <AnimeCard key={anime.id} anime={anime} />
                  ))}
                </div>
                <div className="mt-8 flex flex-col items-center gap-3">
                  {hasMore ? (
                    <button
                      type="button"
                      onClick={() => setVisible((v) => v + PAGE_SIZE)}
                      className="h-10 px-6 rounded-md bg-primary-900 hover:bg-primary-800 text-background-50 text-[13px] font-semibold transition-colors whitespace-nowrap cursor-pointer"
                    >
                      加载更多（剩余 {filtered.length - visible} 部）
                    </button>
                  ) : (
                    <p className="text-[12px] text-foreground-500">已经到底啦，共 {filtered.length} 部作品</p>
                  )}
                  <div className="flex items-center gap-1.5">
                    {Array.from({ length: Math.ceil(filtered.length / PAGE_SIZE) }).map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setVisible((i + 1) * PAGE_SIZE)}
                        className={`w-8 h-8 rounded-md text-[12px] font-semibold transition-colors cursor-pointer ${
                          visible >= (i + 1) * PAGE_SIZE
                            ? "bg-primary-100 text-primary-800"
                            : "bg-background-100 text-foreground-600 hover:bg-background-200"
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}